import {
  Button,
  DataTable,
  FlexBox,
  IconButton,
  Page,
  Text,
} from '@dixa/lib-shared-habla';

import {
  intentDetectionApi,
  IntentDetection as IntentDetectionT,
} from 'apis/intentDetection';
import { appActions } from 'app/app.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { IntentDetectionModal } from '../modal/intentDetectionModal';

export const IntentDetection = (): JSX.Element => {
  const dispatch = useAppDispatch();

  const {
    intentDetections,
    isLoading: readIntentDetectionIsLoading,
    isSuccess: readIntentDetectionIsSuccess,
  } = intentDetectionApi.useReadIntentDetectionsQuery(
    {},
    {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        intentDetections: [...(data?.intentDetections ?? [])].sort((a, b) => {
          return a?.name ? a.name.localeCompare(b?.name ?? '') : 0;
        }),
      }),
    },
  );

  const openIntentDetectionModal = (intentDetection?: IntentDetectionT) => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:intentDetection',
          arg: {
            intentDetection: intentDetection,
          },
        },
      }),
    );
  };

  const [deleteIntentDetection] =
    intentDetectionApi.useDeleteIntentDetectionMutation();
  const notify = useNotifications();

  const handleDeleteIntentDetection = async (
    intentDetection: IntentDetectionT,
  ) => {
    const { isConfirmed, hide } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: 'Delete intent?',
            description: `Are you sure you want to permanently delete the intent "${intentDetection.name}"?
          This action cannot be undone.`,
            confirm: {
              label: 'Yes, delete it now',
              async: true,
              asyncLabel: 'Deleting intent...',
            },
          },
        },
      }),
    );
    if (isConfirmed) {
      try {
        await deleteIntentDetection({
          id: intentDetection.id,
        }).unwrap();
        hide();

        notify(
          {
            title: 'Success',
            text: 'The intent detection has been deleted.',
            icon: 'done',
            time: 5000,
          },
          NotificationType.Success,
        );
      } catch {
        hide();

        notify(
          {
            title: 'Error while deleting intent detection.',
          },
          NotificationType.Error,
        );
      }
    }
  };

  return (
    <>
      <Page.Section
        title="All intents"
        controls={
          <Button variant="primary" onClick={() => openIntentDetectionModal()}>
            Add intent
          </Button>
        }
      >
        <DataTable
          data={intentDetections ?? []}
          tableLoadingStrategy="spinner"
          tableStatusProps={{
            loading: readIntentDetectionIsLoading,
            loadingFailed: !readIntentDetectionIsSuccess,
            emptyState:
              readIntentDetectionIsSuccess &&
              (intentDetections ?? []).length < 1
                ? {
                    title: 'No intent detection',
                    description: 'Click "Add intent" to get started.',
                  }
                : undefined,
          }}
          columns={[
            {
              label: 'Name',
              selector: ({ name, description }) => (
                <FlexBox flexDirection="column" gap="100">
                  <FlexBox>
                    <Text.Paragraph variant="3">{name}</Text.Paragraph>
                  </FlexBox>
                  <FlexBox>
                    <Text.Paragraph variant="4">{description}</Text.Paragraph>
                  </FlexBox>
                </FlexBox>
              ),
            },
            {
              label: '',
              selector: (intentDetection) => (
                <FlexBox flexDirection="row">
                  <IconButton
                    variant="tertiary"
                    icon="pen"
                    onClick={() => openIntentDetectionModal(intentDetection)}
                  />
                  <IconButton
                    variant="tertiary"
                    icon="trash"
                    onClick={() => handleDeleteIntentDetection(intentDetection)}
                  />
                </FlexBox>
              ),
              style: {
                width: 0,
              },
            },
          ]}
        />
      </Page.Section>

      <IntentDetectionModal />
    </>
  );
};
